import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  ComposedModal,
  ContentSwitcher,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Search,
  Switch,
} from "@carbon/react";
import { Loader } from "..";
import { useCategoriesByType } from "../../hooks/useCategories";
import { useAssignManualCategories } from "../../hooks/useAISupplierProfile";
import { useLanguage } from "../../hooks";
import { useNotification } from "../../context/hooks";
import { CategoryResponse, MaterialType } from "../../types";
import "./categoryPickerModal.scss";

interface CategoryPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Category ids the seller already has — hidden from the picker. */
  assignedCategoryIds: Set<string>;
}

const TYPE_ORDER: MaterialType[] = [
  MaterialType.DIRECT_GOODS,
  MaterialType.INDIRECT_GOODS,
  MaterialType.SERVICES,
];

const TYPE_LABEL_KEY: Record<string, string> = {
  [MaterialType.DIRECT_GOODS]: "opportunities.dashboard.directGoods",
  [MaterialType.INDIRECT_GOODS]: "opportunities.dashboard.indirectGoods",
  [MaterialType.SERVICES]: "opportunities.dashboard.services",
};

/**
 * The "add categories" picker for the seller (opportunity dashboard). Browse the material-category
 * catalog by type, filter by name, multi-select, and assign to the current seller — the write
 * counterpart the shipped FE never had (assign endpoint existed backend-only). Assigning refreshes
 * the assigned list + the opportunity dashboard (via `useAssignManualCategories`'s invalidation).
 */
const CategoryPickerModal = ({
  open,
  onClose,
  assignedCategoryIds,
}: CategoryPickerModalProps) => {
  const { t } = useTranslation();
  const langEs = useLanguage();
  const { openNotification } = useNotification();

  const [type, setType] = useState<MaterialType>(MaterialType.DIRECT_GOODS);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useCategoriesByType(type);
  const categories = (data as CategoryResponse[] | undefined) ?? [];

  // Some catalog rows have a null name in one language — fall back to the other, then to empty, so
  // filtering/sorting never dereferences null.
  const categoryName = (c: CategoryResponse) =>
    (langEs ? c.name : c.nameEn) || c.nameEn || c.name || "";

  const available = useMemo(() => {
    const term = search.trim().toLowerCase();
    return categories
      .filter((c) => !assignedCategoryIds.has(c.id))
      .filter((c) => !term || categoryName(c).toLowerCase().includes(term))
      .sort((a, b) => categoryName(a).localeCompare(categoryName(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, assignedCategoryIds, search, langEs]);

  const reset = () => {
    setSelected(new Set());
    setSearch("");
    setType(MaterialType.DIRECT_GOODS);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: assign, isPending } = useAssignManualCategories(
    () => {
      openNotification(t("aiProfile.picker.success"));
      handleClose();
    },
    () => openNotification(t("aiProfile.picker.error"))
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleTypeChange = (nextType: MaterialType) => {
    setType(nextType);
    setSearch("");
  };

  return (
    <ComposedModal open={open} onClose={handleClose} size="md">
      <ModalHeader title={t("aiProfile.picker.title")} />
      <ModalBody hasScrollingContent>
        <ContentSwitcher
          className="category-picker__switcher"
          selectedIndex={TYPE_ORDER.indexOf(type)}
          onChange={({ index }) =>
            handleTypeChange(TYPE_ORDER[index as number] ?? TYPE_ORDER[0])
          }
        >
          {TYPE_ORDER.map((typeOption) => (
            <Switch key={typeOption} text={t(TYPE_LABEL_KEY[typeOption])} />
          ))}
        </ContentSwitcher>

        <Search
          className="category-picker__search"
          size="lg"
          labelText={t("cta.search")}
          placeholder={t("cta.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {isLoading ? (
          <div className="category-picker__loading">
            <Loader />
          </div>
        ) : available.length === 0 ? (
          <p className="category-picker__empty">{t("aiProfile.picker.empty")}</p>
        ) : (
          <fieldset className="category-picker__list">
            {available.map((category) => (
              <Checkbox
                key={category.id}
                id={`category-${category.id}`}
                labelText={categoryName(category)}
                checked={selected.has(category.id)}
                onChange={() => toggle(category.id)}
              />
            ))}
          </fieldset>
        )}
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={handleClose}>
          {t("cta.cancel")}
        </Button>
        <Button
          kind="primary"
          disabled={selected.size === 0 || isPending}
          onClick={() => assign(Array.from(selected))}
        >
          {selected.size > 0
            ? t("aiProfile.picker.assignCount", { count: selected.size })
            : t("aiProfile.picker.assign")}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

export default CategoryPickerModal;
